import { PopoverInteractionKind, Position } from '@blueprintjs/core';
import { Popover2, Tooltip2 } from '@blueprintjs/popover2';
import { Button, Label, StitchesCSS, styled } from '@moleculeone/ui';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';

import { useHasAnalogsAccess } from '@f/hooks/useHasAnalogsAccess';

const ActionButtonsContainer = styled('div', {
  padding: '$4',
  borderRadius: '$medium',
  backgroundColor: 'white',
  boxShadow: 'hsl(206 22% 7% / 35%) 0px 10px 38px -10px, hsl(206 22% 7% / 20%) 0px 10px 20px -15px',
  display: 'flex',
  gap: '$4',
  alignItems: 'center',
});

const PopoverContent = styled('div', {
  padding: '0.2em 0.6em',
  fontSize: '0.9rem',
});

interface ActionButtonsProps {
  onTargetIndexChange: (indexDiff: number) => void;
  currentTargetIndex: number;
  targetCount: number;
  onNewEmptyTargetAdd: () => void;
  onTargetDuplicate: () => void;
  isAnalogGenerationOpen: boolean;
  setAnalogGenerationOpen: (isGenerating: boolean) => void;
  smiles: string;
  name: string;
  css?: StitchesCSS;
}

export function ActionButtons(props: ActionButtonsProps) {
  const {
    onTargetIndexChange,
    currentTargetIndex,
    targetCount,
    onNewEmptyTargetAdd,
    onTargetDuplicate,
    isAnalogGenerationOpen,
    setAnalogGenerationOpen,
    smiles,
    name,
    css,
  } = props;
  const hasAnalogsAccess = useHasAnalogsAccess();

  const [isAddingNewTargetPopoverOpen, setIsAddingNewTargetPopoverOpen] = useState(false);
  const [isDuplicatingTargetPopoverOpen, setIsDuplicatingTargetPopoverOpen] = useState(false);

  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const closePopoverWithTimer = (setter: Dispatch<SetStateAction<boolean>>) => {
    setter(true);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      setter(false);
    }, 1200);
  };

  const handleNewEmptyTargetAdd = () => {
    onNewEmptyTargetAdd();
    closePopoverWithTimer(setIsAddingNewTargetPopoverOpen);
    setIsDuplicatingTargetPopoverOpen(false);
  };

  const handleTargetDuplicate = () => {
    onTargetDuplicate();
    closePopoverWithTimer(setIsDuplicatingTargetPopoverOpen);
    setIsAddingNewTargetPopoverOpen(false);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft' && currentTargetIndex > 0) {
        onTargetIndexChange(-1);
      } else if (event.key === 'ArrowRight' && currentTargetIndex < targetCount - 1) {
        onTargetIndexChange(1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentTargetIndex, onTargetIndexChange, targetCount]);

  return (
    <ActionButtonsContainer css={css}>
      <Button
        theme="slate"
        weight="minimal"
        icon="chevron_left"
        onClick={() => onTargetIndexChange(-1)}
        disabled={isAnalogGenerationOpen || currentTargetIndex < 1}
      />
      <Label>{currentTargetIndex + 1}</Label>
      <Button
        theme="slate"
        weight="minimal"
        icon="chevron_right"
        onClick={() => onTargetIndexChange(1)}
        disabled={isAnalogGenerationOpen || currentTargetIndex >= targetCount - 1}
      />

      <Popover2
        content={<PopoverContent>New target added!</PopoverContent>}
        placement={Position.TOP}
        interactionKind={PopoverInteractionKind.CLICK}
        isOpen={isAddingNewTargetPopoverOpen}
      >
        <Tooltip2 content={'Add new target'} placement={Position.TOP}>
          <Button
            theme="slate"
            weight="outlined"
            icon="add"
            disabled={isAnalogGenerationOpen || !(smiles || name)}
            onClick={handleNewEmptyTargetAdd}
          />
        </Tooltip2>
      </Popover2>

      {hasAnalogsAccess && (
        <Tooltip2 content={'Generate analogs'} placement={Position.TOP}>
          <Button
            theme="purple"
            weight={isAnalogGenerationOpen ? 'solid' : 'outlined'}
            icon="hub"
            disabled={!(smiles || name)}
            onClick={() => {
              setAnalogGenerationOpen(!isAnalogGenerationOpen);
            }}
          />
        </Tooltip2>
      )}

      <Popover2
        content={<PopoverContent>Target duplicated!</PopoverContent>}
        placement={Position.TOP}
        interactionKind={PopoverInteractionKind.CLICK}
        isOpen={isDuplicatingTargetPopoverOpen}
      >
        <Tooltip2 content={'Duplicate target'} placement={Position.TOP}>
          <Button
            theme="slate"
            weight="outlined"
            icon="content_copy"
            disabled={isAnalogGenerationOpen || !(smiles || name)}
            onClick={handleTargetDuplicate}
          />
        </Tooltip2>
      </Popover2>
    </ActionButtonsContainer>
  );
}
